import React, { useMemo } from 'react';

import Tooltip from '../../library/tooltip/tooltip';
import Typography from '../../library/typography/typography';
import CalculatorRegularIcon from '../../shared/icons/calculator-regular-icon';
import TreadwellBarRow from '../treadwell-bar-row/treadwell-bar-row';

import { Maybe } from '../../shared/types/graphql-types';

import './cost-per-mile-info.scss';

interface CostPerMileInfoProps {
  costPerMile: number;
  showTooltip?: boolean;
  title?: Maybe<string>;
  tooltip?: Maybe<string>;
  totalCostOfOwnershipRating: number;
}

function CostPerMileInfo(props: CostPerMileInfoProps) {
  const {
    costPerMile,
    showTooltip = false,
    title: titleProp,
    tooltip: tooltipProp,
    totalCostOfOwnershipRating
  } = props;

  const formattedValue = (costPerMile * 100).toFixed(2);
  const title = titleProp || 'Cost Per Mile';
  const tooltip =
    tooltipProp ||
    'A per‑mile cost metric capturing the full life-cycle expense of a tire—including initial purchase, installation, replacement, fuel consumption, and wear at high mileage.';

  const mappedValue = useMemo(() => {
    const cappedValue = Math.max(0, Math.min(5, totalCostOfOwnershipRating));
    let value = 0;

    //TCO rating has a different distribution where values between 2-4 should account for 80% of the scale.
    if (cappedValue <= 2) {
      value = cappedValue * 10;
    } else if (cappedValue <= 4) {
      value = 20 + (cappedValue - 2) * 30;
    } else {
      value = 80 + (cappedValue - 4) * 20;
    }

    return value;
  }, [totalCostOfOwnershipRating]);

  return (
    <div data-testid="cost-per-mile-info" styleName="container">
      <div styleName="title">
        <CalculatorRegularIcon />
        <Typography weight="bold">{title}</Typography>
        {showTooltip && <Tooltip content={tooltip} />}
      </div>
      <TreadwellBarRow
        formattedValue={`${formattedValue} cents`}
        hideFill
        maxRatingLabel="lower cost"
        minRatingLabel="higher cost"
        scale={100}
        showValueMarker
        showZeroValue
        styleName="cost-per-mile"
        value={mappedValue}
      />
    </div>
  );
}

export default CostPerMileInfo;
